import React, { useEffect, useState } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { getRemotedbUrl, i18nIntl } from '../../lib/ep.js';
import { useCSS } from '../../lib/hooks.js';
import { shuffle as _shuffle } from 'lodash';

import ActionLink from '../builder/ActionLink';
import { BackButton } from '../builder/ActionButton';
import ButtonCollection from '../builder/ButtonCollection.js';
import Checkbox from '../builder/Checkbox';
import ConfirmButton from '../builder/ConfirmButton';
import { AlertMessage } from '../builder/Message';
import Model from '../builder/Model';
import SaveButton from '../builder/SaveButton';
import SectionTitle from '../builder/SectionTitle';
import Select from '../builder/Select';
import Spinner from '../builder/Spinner';
import TextArea from '../builder/TextArea';
import TabledItemsList, { Column, RowTitle } from '../builder/TabledItemsList.js';

const max_insert = 200;

const CTAs = (props) => {
	const disable_confirm = props?.disable_confirm ?? false;

	return (
		<>
			<ConfirmButton
				onClick={(e, cb) => (disable_confirm) ? null : props.makeQuestions(cb)}
				confirmMessage={props?.confirm_message ?? "I have reviewed the generated questions and its answers and I want to continue"}
				_position="top"
				disabled={disable_confirm}
			>{props?.confirm_label ?? "Confirm and create questions"}</ConfirmButton>

			<ConfirmButton
				onClick={() => props.resetAndBack()}
				secondary={true}
				_position="top"
			>{props?.cancel_label ?? "Reset Questions"}</ConfirmButton>
		</>
	)
};

const SectionContents = ({ content, model, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const display_main_options = props?.display_main_options ?? true;
	const import_from_ai = props?.import_from_ai ?? true;

	if(content.size > max_insert) return null;
	const max_cols_x_row = props?.max_cols_x_row ?? 6;

	const col_separators = [
		{ k:'tab', v:i18n("Tabulation") },
		{ k:'semicolon', v:i18n("Semi-colon") },
		{ k:'colon', v:i18n("Comma") },
	];

	return (
		<>
			{(display_main_options) && (
				<Model classname="gui_equal_columns gui_padded_children_low gui_not_in_bottom" model={model} change={change}>
					<Checkbox
						_class={{other:'gui_not_in_top_tiny'}}
						name="has_headers"
					>Imported file has headers</Checkbox>

					<Select
						_class={{ first:true, size:30 }}
						name="col_separator"
						values={col_separators}
					>Columns are delimited by</Select>
				</Model>
			)}

			{(import_from_ai) && (
				<PreviewTable
					content={content}
					max_cols_x_row={max_cols_x_row}
					intl={props.intl}
					block_items={props?.block_items ?? []}
					onChangeStatus={props.onChangeStatus}
				/>
			)}

			{(!import_from_ai) && (
				<PreviewTableOld
					content={content}
					max_cols_x_row={max_cols_x_row}
					intl={props.intl}
				/>
			)}
		</>
	)
};

export const PreviewTable = ({ content, ...props }) => {
	useCSS('comp/sweeps.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const max_height = props?.max_height ?? 'calc(100dvh - 340px)';
	const enable_add = props?.enable_add ?? true;
	const row_icon = props?.row_icon ?? 'knk-questions';
	const column_ok_icon = props?.column_ok_icon ?? 'ep-check-list-item greenIcon';
	const column_ko_icon = props?.column_ko_icon ?? 'ep-delete-circle redIcon';

	
	const block_items = props?.block_items ?? [];
	const [ disabled_items, setDisabledItems ] = useState(block_items);
	const toogleItem = (id) => {
		let _disabled_items = [...disabled_items];

		if(_disabled_items.length > 0 && _disabled_items.includes(id)) {
			_disabled_items = _disabled_items.filter((i) => {
				return (i != id);
			});
		} else {
			_disabled_items = [...disabled_items, id];
		}

		setDisabledItems(_disabled_items);
		if(props.onChangeStatus != undefined)props.onChangeStatus(_disabled_items);
	};

	const column_defs = ['4ch', '3fr'];
	const columns = [
		<Column title='#' type='counter'>
			{(item) => {
				return (
					<div>
						{item[0]}
					</div>
				)
			}}
		</Column>,
		<Column icon={row_icon} title={i18n(content?.headers?.[0] ?? "Question")} main={true}>
			{(item) => {
				return (
					<div>
						<RowTitle>{item[1]}</RowTitle>
					</div>
				)
			}}
		</Column>,
	];
	
	(content?.headers ?? []).map((header, h) => {
		if(h < 1) return null;
		column_defs.push('1fr');
		columns.push(<Column title={i18n(content?.headers?.[h])}>
			{(item) => {
				const classes = (h === 1) ? column_ok_icon : column_ko_icon;

				return (
					<div class={(classes !== false && classes != '') ? classes : null}>
						{item[h + 1]}
					</div>
				)
			}}
		</Column>);
	});

	if(enable_add) {
		column_defs.push('100px');
		columns.push(<Column
			title={i18n("Add?")}
			type='actions'
		>{(item) => {
			const actual_id = parseInt(item[0]) - 1;
			const disabled = (block_items.includes(actual_id)) ? true : false;

			return (
				<ButtonCollection>
					<Checkbox
						disabled={disabled}
						value={(!disabled_items.includes(actual_id) && !block_items.includes(actual_id)) ? true : false}
						onClick={() => (disabled) ? null : toogleItem(actual_id)}
						_class={{ first:true }}
						autotranslate={false}
					>{block_items.includes(actual_id) ? i18n("Repeated") : "&nbsp;"}</Checkbox>
				</ButtonCollection>
			)
		}}</Column>);
	}

	return (
		<TabledItemsList
			table_id='imported_file'
			items={content.f_extract}
			columns={columns}
			disabled_items={disabled_items}
			block_items={block_items}
			max_height={max_height}
			column_defs={{
				gridTemplateColumns:props?.column_defs ?? column_defs.join(" "),
			}}
		/>
	)
};

const PreviewTableOld = ({ content, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const max_height = props?.max_height ?? 'calc(100dvh - 340px)';
	const max_cols_x_row = props?.max_cols_x_row ?? 6;

	return (
		<div id="file_content" class="file_preview" style={{ maxHeight:max_height }}>
			<div class="table_content">
				<ul>
					<div class="thead">
						<li />

						{content.f_headers && content.f_headers.map((row1, i) => {
							return row1.map((row2, j) => {
								if(j > max_cols_x_row || j == 0) return null;

								return (
									<li key={`header_${j}`} class='whiteColor'>
										<span>{(cleanString(row2) != '') ? row2 : props.intl.formatMessage({ id:"Column #{column}" }, { column:j })}</span>
									</li>
								)
							})
						})}

						{!content.f_headers && content.f_extract.map((row1, i) => {
							if(i > 0) return null;

							return row1.map((row2, j) => {
								if(j > max_cols_x_row) return null;

								if(j !== 0) {
									let value_quiz;
									let _class = 'is_main_field whiteIcon ';

									switch(j) {
										case 1:
											value_quiz = "Question";
											_class += 'knk-questions';
											break;

										case 2:
											value_quiz = "Correct answer";
											_class += 'knk-on';
											break;

										default:
											value_quiz = "Incorrect answer";
											_class += 'knk-off';
											break;
									}

									return (
										<li key={`header${j}`} class='row_id whiteColor'>
											<span>{i18n(value_quiz)}</span>

											<span class={_class} />
										</li>
									)
								}
							})
						})}
					</div>

					{content.f_extract.map((row1, i) => {
						return (
							<div key={`row_${i}`}>
								{(row1.map((row2, j) => {
									if(j > max_cols_x_row) return null;

									const classes = [];
									if(j == 0) {
										classes.push('row_id');
									} else if(j > 1 && row2 != '') {
										classes.push('with_icon');
										classes.push((j == 2) ? 'ep-check-list-item greenIcon' : 'ep-delete-circle redIcon');
									}

									return (
										<li key={`id_${i}_${j}`} class={classes.join(" ")}>{row2}</li>
									)
								}))}
							</div>
						)
					})}
				</ul>
			</div>
		</div>
	)
};

const TriviaBulkCreate = (props) => {
	useCSS('comp/sweeps.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const in_layer = props?.in_layer ?? false;
	const import_from_ai = props?.from_ai ?? false;

	const default_file_content = {
		col_separator:props?.default_sepator ?? 'tab',
		has_headers:import_from_ai,
	};

	const promotion_id = props?.promotion?.id ?? (props?.promotion_id ?? false);
	const type = props?.type ?? false;

	const [ display_questions_progress, setDisplayQuestionsProgress ] = useState(false);
	const [ display_table, setDisplayTable ] = useState(false);
	const [ file, setFile ] = useState(false);
	const [ file_content, setFileContent ] = useState(default_file_content);
	const [ model, setModel ] = useState({ text:(props?.text ?? "") });
	const [ disabled_items, setDisabledItems ] = useState([]);

	const link_zendesk = `${window.zendesk_base_url}360052040051`;

	const max_rows_in_preview = props?.max_rows_in_preview ?? 12;
	const max_cols_x_row = (props?.max_answers ?? 5) + 1; //question + 5 answers
	const min_cols_x_row = 3; //question + 2 anwers
	const max_large_question = 150;
	const block_items = props?.already ?? [];

	useEffect(() => {
		if(import_from_ai) importFromFile();
	}, []);

	const change = (k, v) => {
		setModel({ ...model, [k]:v });
	};

	const changeContent = (k, v) => {
		const _file_content = { ...file_content, [k]:v };
		setFileContent(_file_content);
		formatCSVFile(_file_content);
	};

	const b64_to_utf8 = (str) => {
		str = str.replace(/\s/g, '');
		try {
			return decodeURIComponent(escape(window.atob(str)));
		} catch (error) {
			try {
				return unescape(atob(str));
			} catch (error) {
				return true;
			}
		}
	};

	const importFromFile = (cb) => {
		const _file_content = { ...file_content };

		if(import_from_ai) {
			_file_content.file = [];
			model.text.map((item) => {
				_file_content.file.push(item.join("\t"));
			});
		} else if(type != 'leadads') {
			// it good formating
			let string_file = '';
			if(type == 'upload') {
				const arr = file.split(",");
				string_file = b64_to_utf8(arr[1]);
			} else {
				string_file = model.text;
			}

			_file_content.file = string_file.split("\n");
		}

		let size = 0;
		let uncompleted_questions=false;
		let long_questions=false;
		const file_content_file = [];

		_file_content.file.map((item, i) => {
			if(item != undefined) {
				item = item.replace(/\s{2,}/g, " ");
				item = item.replace(/\t/g, " ");
				item = item.toString().replace(/(\r\n|\n|\r)/g, "").trim();
				if(item.length < min_cols_x_row) {
					uncompleted_questions = true;
				} else if(item[0] != undefined && item[0].length > max_large_question) {
					long_questions = true;
				} else {
					size++;
					file_content_file.push(_file_content.file[i]);
				}
			}
		});

		if(!uncompleted_questions && !long_questions) {
			_file_content.file = file_content_file;
			_file_content.size = size;
			setFileContent(_file_content);

			setDisplayTable(true);
			formatCSVFile(_file_content);
			resetFiles();
			return;
		}

		if(uncompleted_questions) {
			alert("There are questions that do not have the minimum of answers");
			if(cb != undefined) cb();
			return;
		}
		
		if(long_questions) {
			alert("There are questions that exceed 150 characters");
			if(cb != undefined) cb();
			return;
		}
	};

	const resetFiles = () => {
		setModel({ text:"" });
		setFile(false);
	};

	const resetAndBack = () => {
		resetFiles();

		if(in_layer && props.go_back != undefined) {
			props.go_back();
			return;
		}

		setDisplayTable(false);
	};

	const getColSeparator = (separator) => {
		switch(separator) {
			case 'semicolon':
				return ';';

			case 'colon':
				return ',';

			case 'tab':
			default:
				return '\t';
		}
	};

	const formatCSVFile = (_file_content) => {
		const f_extract = [];
		let f_headers = [];

		const col_sep = getColSeparator(_file_content.col_separator);

		let remove_index = 0;
		if(_file_content.remove_index != undefined && _file_content.remove_index > 0) {
			remove_index = _file_content.remove_index;

			_file_content.file.map((item, i) => {
				const tnn = item.split(col_sep);
				tnn.splice(remove_index - 1, 1);

				_file_content.file[i] = tnn.join(col_sep);
			});

			delete _file_content.remove_index;
		}

		let splice_ini = 0;

		if(_file_content.has_headers) {
			splice_ini = 1;
			const new_headers = [];
			const headers = _file_content.file.slice(0, 1);
			headers.map((item, i) => {
				f_headers.push((`${i + 1}${col_sep}${item}`).split(col_sep));
				new_headers.push(item.split(col_sep)); // no initial number
			});

			_file_content.headers = new_headers[0];
		} else {
			f_headers = false;
			_file_content.headers = false;
		}

		const results = _file_content.file.slice(splice_ini, splice_ini + max_rows_in_preview);

		results.map((item, i) => {
			f_extract.push((`${(i + 1)}${col_sep}${item}`).split(col_sep));
		});

		const obj = {
			f_extract,
			f_headers,
		};

		const size = _file_content.file.length - splice_ini;
		if(size > max_rows_in_preview) {
			if(size > max_rows_in_preview + 1) {
				const foo = [];
				for(let i = 0; i < f_extract[0].length; i++) {
					foo.push("...");
				}
				f_extract.push(foo);
			}

			const last_element = _file_content.file.slice(-1).pop();
			f_extract.push((`${size}${col_sep}${last_element}`).split(col_sep));
		}

		obj.ap_available = (size <= max_insert) ? true : false;

		if(props.onImportParticipants != undefined) {
			props.onImportParticipants({
				num_cols:_file_content.f_extract[0].length - 1,
				num_rows:(_file_content.has_headers) ? (_file_content.size - 1) : _file_content.size,
			});
		}

		const merged_all = { ..._file_content, ...obj };
		setFileContent(merged_all);
	};

	const makeQuestions = (cb) => {
		const questions = [];

		const col_sep = getColSeparator(file_content.col_separator);
		const splice_ini = (file_content.has_headers) ? 1 : 0;

		let results = file_content.file.slice(splice_ini, splice_ini + file_content.file.length);

		let uncompleted_questions = false;
		let long_questions = false;

		results.map((item, i) => {
			const splitted = item.split(col_sep);

			if(disabled_items.includes(i)) return;

			if(splitted.length < min_cols_x_row) {
				uncompleted_questions = true;
			} else if(splitted[0] != undefined && splitted[0].length > max_large_question) {
				long_questions = true;
			}

			if(splitted[0] != '' && splitted[0].length > 0) {
				const item_to_push = {
					Question:{
						answers_row:0,
						config:{},
						is_advanced:0,
						multianswer:0,
						promotion_id:promotion_id,
						question:splitted[0],
						stage:props.stage_id,
						type:1,
					}
				};

				splitted.shift();

				// set correct
				splitted.map((ans, index) => {
					if(ans != "" && ans.length > 0 && index < (max_cols_x_row - 1)) {
						if(item_to_push.Answer == undefined) item_to_push.Answer = [];

						item_to_push.Answer.push({
							answer:ans,
							config:{
								type:'closed_text',
							},
							correct:(index == 0) ? '1' : '0',
							promotion_id,
						});
					}
				});

				item_to_push.Answer = _shuffle(item_to_push.Answer);
				questions.push(item_to_push);
			}
		});

		if(uncompleted_questions) {
			alert("There are questions that do not have the minimum of answers");
			if(cb != undefined) cb();
			return;
		}

		if(long_questions) {
			alert("There are questions that exceed 150 characters");
			if(cb != undefined) cb();
			return;
		}

		results = null;

		const questions_stringify = JSON.stringify(questions);
		
		setDisplayQuestionsProgress(true);
		
		$.post(getRemotedbUrl(`/quizs/savebulk/${promotion_id}`), { questions:questions_stringify }, (res) => {
			// start fetch process
			if(props.transferQuestions != undefined) {
				props.transferQuestions(res, () => {
					if(props.onCreate != undefined) {
						props.onCreate();
						if(cb != undefined) cb();
						return;
					}

					if(props.back_url != undefined) {
						window.location.hash = props.back_url;
					}
				});
			}
		}, 'JSON');
	};

	const total_questions = file_content?.f_extract?.length ?? 0;

	const fields = [];
	if(file_content.f_headers) {
		file_content.f_headers.map((row1, i) => {
			if(i == 0) {
				row1.map((row2, j) => {
					if(j > 0) {
						let value = '';
						if(cleanString(row2) != '') {
							value = cleanString(row2);
						} else {
							value = props.intl.formatMessage({ id:"Column #{column}" }, { column:j });
						}

						fields.push({ k:j, v:value });
					}
				});
			}
		});
	} else if(file_content.f_extract) {
		file_content.f_extract.map((row1, i) => {
			if(i == 0) {
				row1.map((row2, j) => {
					if(j > 0) {
						fields.push({ k:j, v:props.intl.formatMessage({ id:"Column #{column}" }, { column:j }) });
					}
				});
			}
		});
	}

	const disable_confirm = (Math.max(disabled_items.length, block_items.length) == total_questions) ? true : false;

	if(in_layer) {
		return (
			<>
				<SectionContents
					content={file_content}
					model={file_content}
					change={changeContent}
					display_main_options={props.display_main_options}
					import_from_ai={import_from_ai}
					intl={props.intl}
					block_items={block_items}
					onChangeStatus={(items) => setDisabledItems(items)}
				/>

				{(props.extra_msg != undefined) && (
					props.extra_msg
				)}

				<ButtonCollection in_floating={true}>
					<CTAs
						makeQuestions={makeQuestions}
						resetAndBack={resetAndBack}
						confirm_message={props.confirm_message}
						confirm_label={props.confirm_label}
						disable_confirm={disable_confirm}
						cancel_label={props.cancel_label}
					/>
				</ButtonCollection>
			</>
		)
	}

	if(!display_table) {
		return (
			<div>
				<SectionTitle>Add questions in bulk</SectionTitle>

				<div class="gui_flexbox_auto gui_padded_children_low">
					<div class="bulk_fullheight" style={{ height:'calc(100dvh - 290px)', flexGrow:'1', flexShrink:'0', flexBasis:'65%' }}>
						<TextArea
							name="text"
							model={model}
							change={change}
							_class={{ first:true, other:'full_textarea' }}
						>Copy and paste your questions here</TextArea>
					</div>

					<div style={{ marginTop:'1em' }}>
						<ol class="gui_margined_children_v in_top">
							<li>
								{i18n("Create the questions in a spreadsheet like Excel")}:
								<ul class="bulleted_list">
									<li>{i18n("Each row in the sheet is one quiz question.")}</li>

									<li>{i18n("Write the question in the first column.")}</li>

									<li>{i18n("Write the correct answer in the second column.")}</li>

									<li>
										<FormattedMessage
											id="Use the following columns for the rest of the answer options (maximum {num} in total)."
											values={{
												num:max_cols_x_row - 1,
											}}
										/>
									</li>
								</ul>
							</li>

							<li>
								<FormattedMessage
									id="You can see an example {link}."
									values={{
										link:<ActionLink
											_href={link_zendesk}
											_target="_blank"
										>here</ActionLink>,
									}}
								/>
							</li>

							<li>
								<FormattedMessage
									id="Copy all the questions and answers from the spreadsheet and paste them in this field. Next, click on the {label} button."
									values={{
										label:`"${i18n("Import")}"`,
									}}
								/>
							</li>

							<li>{i18n("The system will show you a preview of the questions so you can check if everything is okay. Once the questions have been imported, you can edit them from the quiz editor.")}</li>

							<li>{i18n("The system allows you to import up to 100 questions in one bulk.")}</li>

							<li>{i18n("When the questions are imported, the system will automatically save them in random order.")}</li>
						</ol>
					</div>
				</div>

				<ButtonCollection in_floating={true}>
					<SaveButton
						disabled={(!model.text) ? true : false}
						onClick={(e, cb) => importFromFile(cb)}
					>Import</SaveButton>

					<BackButton
						onClick={props.go_back}
					/>
				</ButtonCollection>
			</div>
		)
	}

	{(display_questions_progress) && (
		<div class="without_buttons">
			<Spinner>Creating questions and answers, please wait.</Spinner>
		</div>
	)}

	return (
		<div>
			<SectionTitle>Preview of the imported data</SectionTitle>

			{(file_content.size > max_insert) && (
				<AlertMessage
					title="Maximum number of questions exceeded"
					_class={{ other:'in_top' }}
				>
					<p>{i18n("You have exceeded the maximum number of questions. It is not possible to continue.")}</p>

					<p>{i18n("Please contact to our sales team")}: <ActionLink _href={`mailto:${i18n("hello@easypromosapp.com")}`}>hello@easypromosapp.com</ActionLink></p>
				</AlertMessage>
			)}

			<SectionContents
				content={file_content}
				model={file_content}
				change={changeContent}
				display_main_options={props.display_main_options}
				import_from_ai={import_from_ai}
				intl={props.intl}
				block_items={block_items}
			/>

			<ButtonCollection in_floating={true}>
				<CTAs
					makeQuestions={makeQuestions}
					resetAndBack={resetAndBack}
					confirm_message={props.confirm_message}
					confirm_label={props.confirm_label}
					cancel_label={props.cancel_label}
				/>
			</ButtonCollection>
		</div>
	)
};

export default injectIntl(TriviaBulkCreate);